import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  ArrowLeft,
  Clock,
  CheckCircle,
  Calendar,
  Star,
  Sparkles
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ConsultationServicePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchService();
  }, [slug]);

  const fetchService = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/consultation-services/${slug}`);
      if (response.ok) {
        const data = await response.json();
        setService(data);
      } else {
        navigate('/consultation-fees');
      }
    } catch (error) {
      console.error('Error fetching service:', error);
      navigate('/consultation-fees');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!service) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <button
          onClick={() => navigate('/consultation-fees')}
          className="flex items-center text-gray-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Consultation Fees
        </button>
      </div>

      {/* Hero Section */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Image */}
            <div className="aspect-[4/3] rounded-2xl overflow-hidden">
              {service.image ? (
                <img
                  src={service.image}
                  alt={service.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-amber-900/30 to-gray-900 flex items-center justify-center">
                  <Sparkles className="w-24 h-24 text-amber-500/50" />
                </div>
              )}
            </div>

            {/* Content */}
            <div>
              <p className="text-amber-400 text-sm uppercase tracking-widest mb-3">
                {service.category}
              </p>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-light text-white mb-6">
                {service.name}
              </h1>
              
              <div className="flex items-baseline gap-2 mb-6">
                <span className="text-4xl font-light text-amber-400">
                  ${service.starting_price}
                </span>
                <span className="text-gray-500">starting price</span>
              </div>

              {service.duration && (
                <div className="flex items-center text-gray-400 mb-6">
                  <Clock className="w-5 h-5 mr-2 text-amber-500" />
                  <span>{service.duration}</span>
                </div>
              )}

              <p className="text-gray-300 text-lg leading-relaxed mb-8">
                {service.description}
              </p>

              <Button
                onClick={() => navigate('/contact')}
                className="bg-amber-500 hover:bg-amber-400 text-black font-medium px-8 py-3 text-lg"
              >
                Book Consultation Via Our Team
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* What's Included */}
      {service.includes && service.includes.length > 0 && (
        <section className="py-16 bg-gray-900/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl md:text-3xl font-light text-white mb-8">
              What's <span className="font-serif italic text-amber-200">Included</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {service.includes.map((item, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-amber-500 mt-1 shrink-0" />
                  <span className="text-gray-300">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Pricing Tiers */}
      {service.pricing_tiers && service.pricing_tiers.length > 0 && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl md:text-3xl font-light text-white mb-8">
              Pricing <span className="font-serif italic text-amber-200">Options</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {service.pricing_tiers.map((tier, idx) => (
                <div
                  key={idx}
                  className={`p-6 rounded-xl border ${
                    tier.popular
                      ? 'bg-amber-500/10 border-amber-500/50'
                      : 'bg-gray-900/50 border-gray-800'
                  }`}
                >
                  {tier.popular && (
                    <div className="flex items-center gap-1 text-amber-400 text-sm mb-3">
                      <Star className="w-4 h-4 fill-amber-400" />
                      <span>Most Popular</span>
                    </div>
                  )}
                  <h3 className="text-xl font-medium text-white mb-2">{tier.name}</h3>
                  <p className="text-3xl font-light text-amber-400 mb-4">${tier.price}</p>
                  {tier.description && (
                    <p className="text-gray-400 text-sm mb-4">{tier.description}</p>
                  )}
                  {tier.features && tier.features.length > 0 && (
                    <ul className="space-y-2">
                      {tier.features.map((feature, fidx) => (
                        <li key={fidx} className="flex items-start gap-2 text-sm text-gray-300">
                          <CheckCircle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {service.faq && service.faq.length > 0 && (
        <section className="py-16 bg-gray-900/30">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl md:text-3xl font-light text-white mb-8 text-center">
              Frequently Asked <span className="font-serif italic text-amber-200">Questions</span>
            </h2>
            <div className="space-y-6">
              {service.faq.map((item, idx) => (
                <div key={idx} className="border-b border-gray-800 pb-6">
                  <h3 className="text-lg font-medium text-white mb-2">{item.question}</h3>
                  <p className="text-gray-400">{item.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-light text-white mb-4">
            Ready to transform your life?
          </h2>
          <p className="text-gray-400 mb-8">
            Book your {service.name} session today and start your journey.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate('/contact')}
              className="bg-amber-500 hover:bg-amber-400 text-black font-medium px-8 py-3"
            >
              Book Consultation Via Our Team
            </Button>
            <Button
              onClick={() => navigate('/contact')}
              variant="outline"
              className="border-gray-700 text-gray-300 hover:text-white hover:bg-gray-800"
            >
              Have Questions? Contact Us
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ConsultationServicePage;
